import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { findOptimalDateWindows, type DateWindow } from '@/utils/optimalDateWindows';
import { getQualityLabel } from '@/utils/shootingQualityScore';

interface OptimalDateSuggestionsProps {
  location: { name: string; latitude: number; longitude: number };
  arrivalDate: Date;
  departureDate: Date;
  onSelectWindow: (arrival: Date, departure: Date) => void;
}

export const OptimalDateSuggestions: React.FC<OptimalDateSuggestionsProps> = ({
  location,
  arrivalDate,
  departureDate,
  onSelectWindow,
}) => {
  const result = useMemo(
    () => findOptimalDateWindows(location, arrivalDate, departureDate),
    [location.latitude, location.longitude, arrivalDate.getTime(), departureDate.getTime()]
  );

  const { currentWindow, alternatives } = result;
  const currentLabel = getQualityLabel(Math.round(currentWindow.avgQualityScore));

  return (
    <div className="space-y-3 p-3 bg-muted/50 border rounded-lg">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-gold shrink-0" />
        <p className="text-sm">
          <span className="font-medium">Shooting conditions</span>
          {' '}
          <span className="text-muted-foreground">
            Your dates: <span className={currentLabel.color}>{currentWindow.avgQualityScore}/10</span> avg
          </span>
        </p>
      </div>

      {alternatives.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Flexible? Nearby windows with new moon darkness:</p>
          {alternatives.map((alt) => (
            <AlternativeCard
              key={alt.startDate.getTime()}
              window={alt}
              onSelect={() => onSelectWindow(alt.startDate, alt.endDate)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function AlternativeCard({
  window: w,
  onSelect,
}: {
  window: DateWindow;
  onSelect: () => void;
}) {
  const label = getQualityLabel(Math.round(w.avgQualityScore));
  const dateRange = w.startDate.getTime() === w.endDate.getTime()
    ? format(w.startDate, 'MMM d')
    : `${format(w.startDate, 'MMM d')} – ${format(w.endDate, 'MMM d')}`;

  return (
    <div className="flex items-center justify-between gap-3 p-2 bg-background rounded-md border">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{dateRange}</p>
        <p className="text-xs text-muted-foreground">
          <span className={label.color}>{w.avgQualityScore}/10</span> avg quality
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onSelect}>
        Use these dates
      </Button>
    </div>
  );
}
